import { useFetch } from "~vue/api";
import { type GlobalProperties } from "~vue/appTypes";
import { inject, type Ref, ref } from "vue";

// Using a global shared label state to save memory.  This opens up the option to skip an API fetch
// if the namespace we want is already present, but that's overkill for now.
const labels: Record<string, Ref<string>> = {};

/**
 * Composable that returns a function allowing the lookup of organization custom label strings.  If
 * labels are already present in the `custom_labels` prop of the `organization_details` injectable,
 * then they will be used immediately as-is.  Otherwise, labels will be fetched from the back-end
 * for the current user's organization.  Either way, the getter function returned by this composable
 * always returns string refs, so even if labels are fetched from the back-end, they should
 * automatically update in views after loading.
 *
 * @param namespace Optional namespace string.  Providing this is recommended to keep lookup keys
 * shorter, and can also reduce the payload size in the event of an API call.
 * @param showMissingKeys Whether or not to show the missing lookup key when a key cannot be found.
 * This is useful during development, so this option defaults to true in debug environments, and
 * false otherwise.
 *
 * @example
 * const getLabel = useCustomLabels("some.namespace")
 *
 * // titleRef will be a ref to the label string with key 'some.namespace'.
 * const titleRef = getLabel()
 *
 * // myKeyRef will be a ref to the label string with key
 * // 'some.namespace.my_key'.
 * const myKeyRef = getLabel("my_key");
 *
 * // nonExistentRef will be a ref to the string
 * // 'some.namespace.i_do_not_exist' if showMissingKeys is true.
 * // Otherwise, it will be a ref to an empty string.
 * const nonExistentRef = getLabel("i_do_not_exist");
 */
export function useCustomLabels(namespace = "", showMissingKeys: boolean | null = null) {
    const isDebug = inject<GlobalProperties>("globalProperties")?.isDebug ?? false;
    if (showMissingKeys == null) {
        showMissingKeys = isDebug;
    }

    const getLabel = (key = "") => {
        const prefixedKey = [namespace, key].filter((s) => s.length).join(".");
        if (!(prefixedKey in labels)) {
            console.warn(`Missing custom label key: '${prefixedKey}'`);
            labels[prefixedKey] = ref(showMissingKeys ? prefixedKey : "");
        }
        return labels[prefixedKey] as Ref<string>;
    };

    const orgDetails = inject<Ref<{ custom_labels: Record<string, string> }>>("organization_details");
    if (orgDetails?.value?.custom_labels) {
        // Best-case: Labels are already available in the organization_details object before the
        // first render.
        for (const key in orgDetails.value.custom_labels) {
            if (!key.startsWith(namespace)) {
                continue;
            }
            labels[key] = ref(orgDetails.value["custom_labels"][key] ?? "");
        }
    } else {
        // Fallback: We don't have an organization_details object, so we have to fetch labels via an
        // API call.  This is less ideal, as there will be some visual jank after the labels load.
        let url = "/api/organization/custom-labels/";
        if (namespace) {
            const params = new URLSearchParams({ namespace });
            url += "?" + params.toString();
        }
        const { onFetchResponse } = useFetch(url).json<Record<string, string>>();
        onFetchResponse(async (response) => {
            const newLabels = await response.json();
            for (const key in newLabels) {
                const newValue = newLabels[key] ?? "";
                const existingRef = labels[key];
                if (existingRef !== undefined) {
                    existingRef.value = newValue;
                } else {
                    labels[key] = ref(newValue);
                }
            }
        });
    }

    return getLabel;
}
